import type { CarrierBridgeResponse } from "../CarrierBridgeData";
import {
  formatContractDate,
  withFallback,
} from "kontrolEdge/helpers/formattersShappedData";

/**
 * One row of the provisioning history, as the detail endpoint returns it
 * (newest first). `status` uses the codes every KE status history shares:
 * 1 active, 2 finished, 3 pending.
 */
export interface CarrierBridgeHistoryRow {
  status: number;
  text: string | null;
  estimatedTime: string | null;
  timestamp: string;
}

/**
 * `GET /v1/kontroledge/carrier-bridge/:id` (chopper-backend CTX-718): the
 * list item plus the legacy free-text tags, the subscription status and the
 * full status history.
 */
export interface CarrierBridgeDetailResponse extends CarrierBridgeResponse {
  tag: string[];
  subscription:
    | (NonNullable<CarrierBridgeResponse["subscription"]> & {
        status?: string | null;
        autoRenew?: string | null;
      })
    | null;
  statusHistory?: CarrierBridgeHistoryRow[];
}

/** What the two detail cards print, already formatted. */
export interface CarrierBridgeDetailView {
  serviceId: string;
  name: string;
  tags: string[];
  carrierName: string;
  portId: number | null;
  portSubid: string;
  location: string;
  vlan: string;
  capacity: string;
  /** Only shown when set: the circuit ID is optional (José, 28-sep). */
  circuitId: string | null;
  term: string;
  autoRenew: string;
  renewDate: string;
  subscriptionStatus: string;
  serviceStatus: string;
  serviceStatusMessage: string | null;
}

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

export function shapeCarrierBridgeDetail(
  detail: CarrierBridgeDetailResponse
): CarrierBridgeDetailView {
  const subscription = detail.subscription;
  const datacenter = detail.port?.datacenter;
  const renewDate =
    subscription?.renewDate && subscription.renewDate !== "N/A"
      ? formatContractDate(subscription.renewDate)
      : "-";

  return {
    serviceId: withFallback(detail.subscriptionId),
    name: detail.name ?? "",
    tags: Array.isArray(detail.tag) ? detail.tag : [],
    carrierName: withFallback(detail.carrier?.name),
    portId: detail.port?.id ?? null,
    portSubid: withFallback(detail.port?.subid ?? detail.port?.name),
    location: datacenter
      ? [datacenter.name ?? datacenter.datacenterId, datacenter.city]
          .filter(Boolean)
          .join(" · ")
      : "-",
    // A null VLAN is the whole port; the approved design shows it as "-".
    vlan: detail.vlan ?? "-",
    capacity: `${detail.mbps} Mbps`,
    circuitId: detail.circuitId?.trim() ? detail.circuitId : null,
    // Term and auto-renew are the subscription's (CTX-805); none yet reads "-".
    term: subscription?.term
      ? `${subscription.term} ${withFallback(subscription.term_unit)}`
      : "-",
    autoRenew: subscription?.autoRenew ?? "-",
    renewDate,
    subscriptionStatus: subscription?.status
      ? capitalize(subscription.status)
      : "-",
    serviceStatus: detail.serviceStatus ?? "unknown",
    serviceStatusMessage: detail.serviceStatusMessage ?? null,
  };
}

/** The history in the shape the Port "Activity" card (`StatusTable`) reads. */
export function toActivityEntries(
  history: CarrierBridgeHistoryRow[] | undefined
): { status: number; text: string; createdAt: string }[] {
  return (history ?? []).map((row) => ({
    status: row.status,
    text: row.text ?? "-",
    createdAt: row.timestamp,
  }));
}
